/**
 * Righe del pannello "Proprieta" (prototipo `Centro Ticket.dc.html` L335-L359),
 * estese al dominio reale: 6 stati, 4 priorita, assegnatari multipli (ruling 6),
 * tag (ruling 12).
 *
 * `canEditPriority` e `canEditTags` derivano dalla presenza dei metodi
 * opzionali sull'adapter (`!!api.setPriority`, `!!api.setTags`): l'area
 * rappresentante non li implementa e le righe restano in sola lettura.
 *
 * Funzione pura: decide SOLO cosa e editabile e con quali opzioni. Le chiamate
 * REST, i toast e la gestione del 409 restano in `PropertiesCard`.
 */
import { PRIORITY_LABELS, STATUS_LABELS, STATUS_TRANSITIONS, TICKET_PRIORITIES } from '../constants';
import { originVisual } from './visual';
import { displayStatus } from '../displayStatus';
import { isLimitedViewer } from './actionsModel';
import { copyGroupOptions } from '../copyGroupsModel';
import type { CopyGroupRef, GroupLite, TicketDetail, TicketParticipant, ViewerRole } from '../../../services/supportApi';

/** Tetto dei tag, speculare a `MAX_TAGS` di `services/supportTicketService.js:544`. */
export const MAX_TAGS = 20;
/** Lunghezza massima del singolo tag (`MAX_TAG_LEN`, stesso file, :545). */
export const MAX_TAG_LEN = 40;

export interface S3PropertyRow {
  key: 'stato' | 'priorita' | 'canale' | 'origine' | 'assegnato' | 'tag';
  label: string;
  kind: 'select' | 'text' | 'tags' | 'assignees';
  value: string;
  options?: { value: string; label: string }[];
}

export interface S3PropertyCtx {
  canHandle: boolean;
  canTransfer: boolean;
  canEditPriority: boolean;
  canEditTags: boolean;
  groups: GroupLite[];
  /**
   * `viewerIsMember` del dettaglio (Task 26). `false` = assegnatario esterno:
   * canale e assegnatari in sola lettura. Assente = true.
   */
  isGroupMember?: boolean;
  /** `ticket.viewerRole` (parte B): partecipante = tutto in sola lettura. */
  viewerRole?: ViewerRole;
}

export const buildPropertyRows = (ticket: TicketDetail, ctx: S3PropertyCtx): S3PropertyRow[] => {
  const transitions = STATUS_TRANSITIONS[ticket.status] || [];
  // Su un ticket chiuso non si tocca piu niente: e la regola del dettaglio
  // attuale (TicketDetail.tsx:546 e :581) ed e anche quella del backend, che
  // risponde 409 a setTags/setPriority/setAssignees/transfer. Le transizioni
  // di `chiuso` sono gia vuote, quindi lo stato ricade da solo in sola lettura.
  const isChiuso = ticket.status === 'chiuso';
  // Partecipante invitato (parte B): legge, non lavora il ticket.
  const canHandle = ctx.canHandle && !isLimitedViewer(ctx.viewerRole);
  const statoEditable = canHandle && transitions.length > 0;
  const otherGroups = (ctx.groups || []).filter((g) => String(g._id) !== String(ticket.groupId?._id));
  // Il cambio canale E un trasferimento: serve il permesso dedicato (ruling 12).
  const member = ctx.isGroupMember !== false;
  const canaleEditable = member && canHandle && ctx.canTransfer && !isChiuso && otherGroups.length > 0;
  const prioEditable = canHandle && ctx.canEditPriority && !isChiuso;
  const tagEditable = canHandle && ctx.canEditTags && !isChiuso;
  const assegnati = (ticket.assignees || [])
    .filter((a) => a.nome)
    .map((a) => (a.external ? `${a.nome} (esterno)` : a.nome));

  return [
    {
      key: 'stato',
      label: 'STATO',
      kind: statoEditable ? 'select' : 'text',
      // Stato mostrato: «Unito» / «Indesiderato» al posto di «Chiuso» (stato salvato invariato).
      value: statoEditable ? ticket.status : displayStatus(ticket).label,
      ...(statoEditable && {
        options: [ticket.status, ...transitions].map((s) => ({
          value: s,
          label: s === ticket.status ? displayStatus(ticket).label : STATUS_LABELS[s] || s,
        })),
      }),
    },
    {
      key: 'priorita',
      label: 'PRIORITÀ',
      kind: prioEditable ? 'select' : 'text',
      value: prioEditable ? ticket.priority : PRIORITY_LABELS[ticket.priority] || ticket.priority,
      ...(prioEditable && {
        options: TICKET_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] })),
      }),
    },
    {
      key: 'canale',
      // Il valore vuoto in modalita select e voluto: la select parte sul
      // canale corrente come opzione neutra, il trasferimento avviene solo
      // scegliendo una destinazione diversa.
      label: 'CANALE',
      kind: canaleEditable ? 'select' : 'text',
      value: canaleEditable ? '' : ticket.groupId?.nome || '',
      ...(canaleEditable && {
        options: otherGroups.map((g) => ({ value: String(g._id), label: g.nome })),
      }),
    },
    { key: 'origine', label: 'ORIGINE', kind: 'text', value: originVisual(ticket.origin).label },
    {
      key: 'assegnato',
      label: 'ASSEGNATO',
      kind: member && canHandle && !isChiuso ? 'assignees' : 'text',
      value: assegnati.length ? assegnati.join(', ') : 'nessuno',
    },
    {
      key: 'tag',
      label: 'TAG',
      kind: tagEditable ? 'tags' : 'text',
      value: (ticket.tags || []).join(', '),
    },
  ];
};

export interface S3ParticipantsSection {
  visible: boolean;
  canAdd: boolean;
  canRemove: boolean;
  items: TicketParticipant[];
}

/**
 * Sezione «Partecipanti» (spec 2026-10-15, parte B): visibile se ci sono
 * partecipanti o se si può invitare. Invito e rimozione solo con
 * `viewerCanInvite` (componente col permesso), l'adapter che li implementa e
 * ticket non chiuso (il backend risponde 409).
 */
export const buildParticipantsSection = (
  ticket: TicketDetail,
  ctx: { canAdd: boolean; canRemove: boolean }
): S3ParticipantsSection => {
  const items = ticket.participants || [];
  const open = ticket.status !== 'chiuso';
  const can = Boolean(ticket.viewerCanInvite) && open;
  const canAdd = can && ctx.canAdd;
  return { visible: items.length > 0 || canAdd, canAdd, canRemove: can && ctx.canRemove, items };
};

export interface S3CopyGroupsSection {
  visible: boolean;
  canManage: boolean;
  items: CopyGroupRef[];
  /** Canali che si possono mettere in copia (vuoto senza gestione). */
  options: GroupLite[];
  /**
   * Responsabile = smistamento: la promozione passa dal trasferimento (lo
   * smistamento non scende mai in copia e chi lo guarda ne perde l'accesso).
   */
  promoteByTransfer: boolean;
}

/**
 * Sezione «In copia» (spec 2026-10-15, parte A): visibile se ci sono copie o se
 * se ne possono aggiungere. Gestione (aggiungi, togli, rendi responsabile) solo
 * a chi vede il canale responsabile (non esterni, partecipanti o componenti in
 * copia) col permesso di trasferimento, con l'adapter che la implementa e su un
 * ticket non chiuso (il backend risponde 409).
 */
export const buildCopyGroupsSection = (
  ticket: TicketDetail,
  ctx: { canTransfer: boolean; groups: GroupLite[]; hasApi: boolean }
): S3CopyGroupsSection => {
  const items = ticket.copyGroups || [];
  const member = ticket.viewerIsMember !== false && !isLimitedViewer(ticket.viewerRole);
  const canManage = member && ctx.canTransfer && ctx.hasApi && ticket.status !== 'chiuso';
  const options = canManage
    ? copyGroupOptions(ctx.groups, { responsibleId: ticket.groupId?._id, exclude: items.map((c) => c.id) })
    : [];
  return {
    visible: items.length > 0 || (canManage && options.length > 0),
    canManage,
    items,
    options,
    promoteByTransfer: ticket.groupId?.inboundDefault === true,
  };
};

/**
 * Normalizzazione client del singolo tag, speculare a `normalizeTags` del
 * backend (`supportTicketService.js:552-561`): trim, minuscolo, taglio a 40.
 * Nessun filtro sui caratteri: il backend non ne applica, e rifiutare qui un
 * tag che il server accetterebbe sarebbe una divergenza.
 */
export const normalizeTagInput = (raw: string): string | null => {
  const t = String(raw || '').trim().toLowerCase().slice(0, MAX_TAG_LEN);
  return t || null;
};
